import React from "react";
import ReactDOM from "react-dom/client";
import "@/fonts";
import "@/index.css";
import "@/i18n"; // Initialize i18next
import App from "@/App";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import { installSafeToast } from "@/utils/safeToast";

// Global guardrail: coerce any non-string/non-JSX toast content to a safe
// string so a raw API error object can never crash the root <Toaster/> (React
// #31 → blank page). See PRD v29.
installSafeToast();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    {/* Root safety net: a crash (including a stale-chunk ChunkLoadError after
        a deploy) recovers with a reload instead of leaving a blank page. */}
    <ChunkErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </ChunkErrorBoundary>
  </React.StrictMode>,
);

// Register service worker for PWA / Field Mode offline support.
//
// Deploy-resilience: `updateViaCache: 'none'` forces the browser to always
// re-fetch sw.js from the network (never the HTTP cache), and we explicitly
// call update() so a newly-deployed SW is picked up promptly. When a new SW
// takes control (`controllerchange`) we reload ONCE so every open tab starts
// running the freshly-deployed bundle — this breaks the "old SW keeps serving
// stale chunks across all my tabs → blank page" trap that a plain refresh
// cannot fix.
if ("serviceWorker" in navigator) {
  // Only auto-reload when an EXISTING controller is replaced by a new one
  // (i.e. a real update). Skips the wasteful reload on a user's first visit.
  const hadController = !!navigator.serviceWorker.controller;
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing || !hadController) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        // Nudge any already-waiting worker to activate immediately.
        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }
        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (
              installing.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              installing.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
        // Check for a newer SW on load and periodically thereafter.
        registration.update();
        setInterval(() => registration.update(), 60 * 60 * 1000);
      })
      .catch((err) => console.warn("SW registration failed:", err));
  });
}
